// Angular imports
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';

// Service imports
import { HelperService } from '../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { TimeExpenseComfirmationService } from '../../_services/time-expense-confirmation.service';
import { ScreenService } from '../../../_services/shared-services/screen.service';

// CC-Common imports
import { CCDeviceService, IconConstants, Utils } from 'cc-common';

// Constant imports
import { Constants } from '../../../_constants/constants';
import { ImageConstants } from '../../../_constants/image-constants';
import { StringFormatConstants } from '../../../_constants/string-format-constants';

// 3rd party imports
import { Observable, Subscription } from 'rxjs';

// Model imports
import { TimeExpenseSubmissionHistoryResultDto } from '../../_models/time-expense-submission-history-result-dto';
import { TimeExpenseSubmissionActionBaseParametersDto } from '../../_models/time-expense-submission-action-base-parameters-dto';

@Component({
    selector: 'app-time-expense-confirmation',
    templateUrl: './time-expense-confirmation.component.html',
    styleUrls: ['./time-expense-confirmation.component.scss']
})
export class TimeExpenseConfirmationComponent implements OnInit, OnDestroy {
    timeExpenseSubmissionHistoryResult: TimeExpenseSubmissionHistoryResultDto;

    submissionId: string;
    userId: string;
    action = '';
    reason = '';
    allowDeny = false;
    allowApprove = false;
    status: any;
    reportUrl: string;
    loadingVisible = false;
    loaderSubscription: Subscription;
    pageTitle = 'Time & Expense Submission';
    dateFormat = StringFormatConstants.dateFormat;
    defaultLabelLocation = Constants.defaultFormLabelLocation;
    ciraLogo = ImageConstants.ccLogo;
    poweredByLogo = ImageConstants.poweredByCCLogo;
    reportDownload = false;
    isSmallScreen = false;
    isLandscape = false;

    iconCheck = IconConstants.CHECK;
    iconCross = IconConstants.CROSS;
    iconWarn = IconConstants.WARN;
    iconFlag = IconConstants.FLAG;
    iconFile = IconConstants.PDF_FILE;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly timeExpenseSubService: TimeExpenseComfirmationService,
        private readonly screenService: ScreenService,
        private readonly deviceService: CCDeviceService
    ) {}

    ngOnInit(): void {
        this.updateScreenSize();
        this.screenService.changed.subscribe(this.updateScreenSize.bind(this));
        this.deviceService.subjectOrientationChanged.subscribe(() => {
            this.isLandscape = this.deviceService.isLandscape();
        });

        this.loaderSubscription = this.timeExpenseSubService.onLoadingEvent().subscribe((showLoading: boolean) => {
            setTimeout(() => (this.loadingVisible = showLoading), 10);
        });
        this.route.queryParamMap.subscribe((data) => {
            let params = data['params'];

            params = Object.keys(params).reduce((c, k) => ((c[k.toLowerCase().trim()] = params[k]), c), {});

            if (Utils.notNullOrEmpty(params)) {
                this.submissionId = params['submissionid'];
                this.userId = params['userid'];
                this.action = params['action'];
            }
        });

        if (Utils.notNullOrEmpty(this.action)) {
            this.getTimeAndExpenseSubmissionDetail();
        } else {
            this.getTimeAndExpenseReport();
        }
    }

    ngOnDestroy(): void {
        if (Utils.notNullAndDefined(this.loaderSubscription)) {
            this.loaderSubscription.unsubscribe();
        }
    }

    getSelectedTabData(): any {
        if (Utils.equalsIgnoreCase(this.timeExpenseSubmissionHistoryResult.selectedTab.name, 'timeExpense')) {
            return this.timeExpenseSubmissionHistoryResult.timeSubmissionItems;
        } else if (Utils.equalsIgnoreCase(this.timeExpenseSubmissionHistoryResult.selectedTab.name, 'generalExpense')) {
            return this.timeExpenseSubmissionHistoryResult.expenseSubmissionItems;
        } else if (Utils.equalsIgnoreCase(this.timeExpenseSubmissionHistoryResult.selectedTab.name, 'mileageExpense')) {
            return this.timeExpenseSubmissionHistoryResult.mileageSubmissionItems;
        }
        return [];
    }

    onNoteUpdate(): void {
        this.getTimeAndExpenseSubmissionDetail();
    }

    downloadReport(): void {
        this.reportDownload = true;
        this.getTimeAndExpenseReport();
    }

    onSubmit(): void {
        if (Utils.isTrue(this.allowDeny)) {
            if (Utils.isNullOrEmpty(this.reason)) {
                this.errorService.updateSubjectErrorMessage('Please provide a deny reason');
            } else {
                this.submitTimeAndExpenseConfirmation();
            }
        } else if (Utils.isTrue(this.allowApprove)) {
            this.submitTimeAndExpenseConfirmation();
        }
    }

    getScreenByWidth = (width) => {
        return this.isSmallScreen ? 'sm' : 'lg';
    };

    private submitTimeAndExpenseConfirmation(): void {
        this.timeExpenseSubService.showLoading();

        const params = new TimeExpenseSubmissionActionBaseParametersDto();
        params.submissionId = this.submissionId;
        params.userId = this.userId;

        let teSubmission: Observable<any>;

        if (Utils.isTrue(this.allowApprove)) {
            teSubmission = this.timeExpenseSubService.approveTimeExpenseSubmission(params);
        } else if (Utils.isTrue(this.allowDeny)) {
            params.denyReason = this.reason;
            teSubmission = this.timeExpenseSubService.denyTimeExpenseSubmission(params);
        }

        teSubmission.subscribe(
            () => {
                this.timeExpenseSubService.hideLoading();
                const success200 = new Object();
                success200['iconText'] = 'SUCCESS';
                success200['statusHeader'] = 'Success';
                const action = Utils.isTrue(this.allowApprove) ? 'approved' : 'denied';
                success200[
                    'statusMsg'
                ] = `${this.timeExpenseSubmissionHistoryResult.referenceNumber} Submission has been successfully ${action}.`;
                this.status = success200;
            },
            (error: HttpErrorResponse) => {
                this.errorService.displayError(error);
                this.timeExpenseSubService.hideLoading();
            }
        );
    }

    private getTimeAndExpenseSubmissionDetail(): void {
        this.timeExpenseSubService.showLoading();

        const baseParams = new TimeExpenseSubmissionActionBaseParametersDto();
        baseParams.submissionId = this.submissionId;
        baseParams.userId = this.userId;
        baseParams.action = this.action;

        this.timeExpenseSubService.getTimeAndExpenseSubmissionDetail(baseParams).subscribe(
            (result: TimeExpenseSubmissionHistoryResultDto) => {
                this.allowApprove = result.allowApprove;
                this.allowDeny = result.allowDeny;
                this.timeExpenseSubmissionHistoryResult = result;
                this.prepareExpenseTab();
                this.timeExpenseSubService.hideLoading();
            },
            (error: HttpErrorResponse) => {
                this.timeExpenseSubService.hideLoading();
                this.errorHandler(error);
            }
        );
    }

    private errorHandler(error: HttpErrorResponse): void {
        if (Utils.equals(error.status, 409)) {
            this.processErrorMsg(error.error);
        } else {
            this.errorService.displayError(error);
        }
    }

    private processErrorMsg(error: string): void {
        const error409 = new Object();
        const errorList = Utils.equalsIgnoreCase(typeof error, 'string') && error.includes('|') ? error.split('|') : [];
        if (errorList.length === 3) {
            error409['iconText'] = errorList[0];
            error409['statusHeader'] = errorList[1];
            error409['statusMsg'] = errorList[2];
            this.status = error409;
        }
    }

    private getTimeAndExpenseReport(): void {
        this.timeExpenseSubService.showLoading();

        const baseParams = new TimeExpenseSubmissionActionBaseParametersDto();
        baseParams.submissionId = this.submissionId;
        baseParams.userId = this.userId;

        this.timeExpenseSubService.getTimeAndExpenseReport(baseParams).subscribe(
            (result: Blob) => {
                if (Utils.notNullAndDefined(result)) {
                    if (Utils.isFalse(this.reportDownload)) {
                        this.reportUrl = this.helperService.getFileUrlFromBlob(result);
                    } else {
                        this.helperService.showBlobInBrowserTab(result, 'TimeAndExpenseSubmissionReport.pdf');
                        this.reportDownload = false;
                    }
                }
                this.timeExpenseSubService.hideLoading();
            },
            (error: HttpErrorResponse) => {
                this.timeExpenseSubService.hideLoading();
                this.errorHandler(error);
            }
        );
    }

    private prepareExpenseTab(): void {
        if (Utils.notNullAndDefined(this.timeExpenseSubmissionHistoryResult)) {
            this.timeExpenseSubmissionHistoryResult['tabItems'] = [];
            if (Utils.notNullOrEmpty(this.timeExpenseSubmissionHistoryResult.timeSubmissionItems)) {
                const tab = new Object();
                tab['name'] = 'timeExpense';
                tab['text'] = 'Time';
                this.timeExpenseSubmissionHistoryResult['tabItems'].push(tab);
            }
            if (Utils.notNullOrEmpty(this.timeExpenseSubmissionHistoryResult.expenseSubmissionItems)) {
                const tab = new Object();
                tab['name'] = 'generalExpense';
                tab['text'] = 'General Expense';
                this.timeExpenseSubmissionHistoryResult['tabItems'].push(tab);
            }
            if (Utils.notNullOrEmpty(this.timeExpenseSubmissionHistoryResult.mileageSubmissionItems)) {
                const tab = new Object();
                tab['name'] = 'mileageExpense';
                tab['text'] = 'Mileage Expense';
                this.timeExpenseSubmissionHistoryResult['tabItems'].push(tab);
            }

            this.timeExpenseSubmissionHistoryResult['selectedTab'] = Utils.notNullOrEmpty(
                this.timeExpenseSubmissionHistoryResult['tabItems']
            )
                ? this.timeExpenseSubmissionHistoryResult['tabItems'][0]
                : null;
        }
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        this.isLandscape = this.deviceService.isLandscape();
    }
}
